/**
 * sessionRosterAdapter.ts
 *
 * File: Mythbound.App/src/pages/sessionRosterAdapter.ts
 * Created: 2026-09-23 (PRD001A R2/R6 frontend chunk)
 *
 * PURPOSE:
 * Pure mapping function from the API's `Character` shape to the small
 * display shape `SessionView.tsx`'s roster cards actually render. Extracted
 * out of `SessionView.tsx` itself (previously inlined in that file's
 * `fetchCharacters` effect) so it can be unit tested directly, without
 * pulling in `SessionView`'s large, unrelated dependency graph (voice,
 * dice, notifications, websocket chat, Chakra) — the same reasoning
 * `src/__tests__/integration/SessionRoute.test.tsx`'s header comment gives
 * for testing `SessionControls` directly instead of the full page.
 *
 * WHY (001A_R2_R6_Contract_Handoff.md, "Session roster" row): preserve the
 * class/level/HP/max_HP distinctions from the API through this adapter to
 * what is actually rendered. FORBIDDEN here: `hp ?? 0`, `max_hp ?? hp`, a
 * default Fighter, a default level 1, and a bogus health-bar fraction. A
 * health ratio is only ever computed when BOTH hp and max_hp are known real
 * values AND max_hp is positive — otherwise `healthRatio` is `null` and the
 * caller must render an honest "unknown" state (no bar, or an empty one),
 * never a fabricated percentage.
 *
 * The previous version of this mapping (`c.hp ?? 0`, `c.max_hp ?? c.hp ?? 0`)
 * silently turned "hidden" and "genuinely unknown" HP into a fabricated 0 —
 * exactly the fabrication PRD001A forbids, and it made a real `hp === 0`
 * (an unconscious character) indistinguishable from "no data at all."
 */
import {
  type Character,
  classifyCharacterField,
  formatCharacterField,
} from '../services/api/CharacterService';

export interface SessionRosterEntry {
  character_id: string;
  /** Formatted display text — real name, or "Hidden"/"Not set" honestly. */
  name: string;
  /** Not real API data yet — the backend does not return race. Pre-existing
   * placeholder, unchanged by this adapter. */
  race: string;
  /** Formatted display text — real class, "Hidden", or "Not set". Never a
   * fabricated "Fighter". */
  classText: string;
  /** Formatted display text — real level, "Hidden", or "Not set". Never a
   * fabricated "1". */
  levelText: string;
  avatar: string;
  /** Formatted display text for current HP — real value (incl. "0"),
   * "Hidden", or "Not set". */
  hpText: string;
  /** Formatted display text for max HP — same three states as `hpText`. */
  maxHpText: string;
  /**
   * Percentage in [0, 100], or `null` when hp/max_hp aren't BOTH known real
   * values with a positive max — the caller must not fabricate a bar when
   * this is `null`.
   */
  healthRatio: number | null;
}

/**
 * Map one API `Character` to the shape `SessionCharacterCard` renders,
 * preserving the undefined (hidden) / null (unknown) / real-value
 * distinction for class/level/hp/max_hp all the way through.
 *
 * @param character The raw API response entry.
 * @param isOwner Whether this entry is the viewer's OWN character — per the
 *   roster endpoint's documented ordering, that is exactly `index === 0`.
 *   An owner's fields are classified as `'value'`/`'unknown'` only, never
 *   `'hidden'` (see `classifyCharacterField`).
 */
export function mapCharacterToRosterEntry(character: Character, isOwner: boolean): SessionRosterEntry {
  const nameDisplay = classifyCharacterField(character.character_name, isOwner);
  const classDisplay = classifyCharacterField(character.character_class, isOwner);
  const levelDisplay = classifyCharacterField(character.level, isOwner);
  const hpDisplay = classifyCharacterField(character.hp, isOwner);
  const maxHpDisplay = classifyCharacterField(character.max_hp, isOwner);

  const bothKnownPositiveMax =
    hpDisplay.kind === 'value' && maxHpDisplay.kind === 'value' && (maxHpDisplay.value as number) > 0;

  return {
    character_id: character.character_id,
    name: formatCharacterField(nameDisplay, (v) => v),
    race: 'Unknown', // Backend doesn't return race yet — pre-existing placeholder.
    classText: formatCharacterField(classDisplay, (v) => v),
    levelText: formatCharacterField(levelDisplay, (v) => String(v)),
    avatar: '',
    hpText: formatCharacterField(hpDisplay, (v) => String(v)),
    maxHpText: formatCharacterField(maxHpDisplay, (v) => String(v)),
    healthRatio: bothKnownPositiveMax
      ? ((hpDisplay.value as number) / (maxHpDisplay.value as number)) * 100
      : null,
  };
}
